import { Box, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { ReactNode } from "react";
import { Link } from "react-router-dom";

/**
 * Group-separates counts here rather than at each call site, so every tile in
 * the row renders the same number the same way.
 */
function formatValue(value: number | string | undefined): string {
  if (typeof value === "number") return value.toLocaleString();
  return value ?? "0";
}

/**
 * The Violations Insights metric tile. A near-copy of Staff's `KeyMetric`, kept
 * separate on purpose: the two differ in the `info` slot and number formatting,
 * and Staff's five callers are outside this page's blast radius.
 */
export function ViolationKeyMetric({
  name,
  value,
  loading,
  href,
  onClick,
  info,
}: {
  name: string;
  value: number | string | undefined;
  loading: boolean;
  href?: string;
  onClick?: () => void;
  /** Optional info affordance rendered beside the name, e.g. a MetricInfoIcon. */
  info?: ReactNode;
}) {
  const theme = useTheme();
  const isClickable = !!href || !!onClick;

  return (
    <Box
      component={href ? Link : "div"}
      to={href ?? ""}
      preventScrollReset={true}
      onClick={onClick}
      sx={{
        textDecoration: "none",
        cursor: isClickable ? "pointer" : "default",
        py: 1.25,
        px: 1.5,
        borderRadius: 1,
        backgroundColor:
          theme.palette.mode === "light" ? theme.palette.grey[50] : theme.palette.grey[900],
        border: `1px solid ${theme.palette.divider}`,
        transition: "all 0.15s ease-in-out",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        minWidth: 0,
        overflow: "hidden",
        "&:hover": isClickable
          ? {
              backgroundColor: theme.palette.action.hover,
              borderColor: theme.palette.action.selected,
              transform: "translateY(-1px)",
            }
          : {},
      }}
    >
      <Typography variant="h5" fontWeight={700} color={theme.palette.text.primary} lineHeight={1.1}>
        {loading ? <Skeleton variant="text" width={35} height={32} /> : formatValue(value)}
      </Typography>
      {/* The name always sits in a row, with or without `info`, so the tile's
          shape doesn't change when one metric gains an explanation. In a narrow
          grid track the name is the item that shrinks, so it carries `title` to
          stay recoverable, and the icon is pinned so it can't take its width. */}
      <Stack direction="row" alignItems="center" spacing={0.25} sx={{ mt: 0.5, minWidth: 0 }}>
        <Typography
          variant="caption"
          color={theme.palette.text.secondary}
          lineHeight={1.3}
          noWrap
          title={name}
          sx={{ minWidth: 0 }}
        >
          {name}
        </Typography>
        {info ? <Box sx={{ display: "flex", flexShrink: 0 }}>{info}</Box> : null}
      </Stack>
    </Box>
  );
}
